import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  #intersectionObserver = null;
  #offscreen = false;
  #lastScrollTop = 0;
  #timeout = null;
  #scrollRafId = null;
  #resizeRafId = null;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0] || this.#resizeRafId !== null) return;

    this.#resizeRafId = requestAnimationFrame(() => {
      this.#resizeRafId = null;
      const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
      document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

      if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
        this.#updateMenuVisibility(false);
      }
    });
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = { threshold: alwaysSticky ? 1 : 0 };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    this.#menuDrawerHiddenWidth = hideMenu ? window.innerWidth : null;
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      this.dataset.scrollDirection = isAtTop ? 'none' : (isScrollingUp ? 'up' : 'down');
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    
    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
    if (this.#resizeRafId !== null) cancelAnimationFrame(this.#resizeRafId);
    
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    let groupRafId = null;
    
    const resizeObserver = new ResizeObserver((entries) => {
      if (groupRafId !== null) return;

      groupRafId = requestAnimationFrame(() => {
        groupRafId = null;
        
        const headerGroupHeight = entries.reduce((totalHeight, entry) => {
          if (
            entry.target !== header ||
            (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
          ) {
            return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
          }
          return totalHeight;
        }, 0);
        
        document.body.style.setProperty('--header-group-height', `${Math.round(headerGroupHeight)}px`);
      });
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      let shouldRecalculate = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          shouldRecalculate = true;
          break;
        }
      }

      if (shouldRecalculate) {
        const currentChildren = headerGroup.children;
        for (let i = 0; i < currentChildren.length; i++) {
          const element = currentChildren[i];
          if (element instanceof HTMLElement) {
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});